import React, { useState } from "react";
import Editor from "@monaco-editor/react";
import { Play, Loader2, CheckCircle2, XCircle, ChevronDown, ChevronUp, Terminal } from "lucide-react";

const MONACO_LANG = { python: "python", javascript: "javascript", java: "java", cpp: "cpp", c: "c" };
const DIFFICULTY_COLOR = { Easy: "text-emerald-400 bg-emerald-400/10", Medium: "text-amber-400 bg-amber-400/10", Hard: "text-rose-400 bg-rose-400/10" };

function TestCaseRow({ tc, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] overflow-hidden" data-testid={`coding-testcase-row-${idx}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-3 py-2 text-left">
        <span className="flex items-center gap-2 text-xs font-semibold text-slate-300">
          {tc.passed ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <XCircle className="w-3.5 h-3.5 text-rose-400" />}
          Test Case {idx + 1} {tc.is_hidden && <span className="text-[10px] uppercase text-slate-500">(hidden)</span>}
        </span>
        {open ? <ChevronUp className="w-3.5 h-3.5 text-slate-500" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-500" />}
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-1.5 text-[11px] font-mono">
          <p className="text-slate-500">Input: <span className="text-slate-300 whitespace-pre-wrap">{tc.input}</span></p>
          <p className="text-slate-500">Expected: <span className="text-slate-300">{tc.expected_output}</span></p>
          <p className="text-slate-500">Got: <span className={tc.passed ? "text-emerald-300" : "text-rose-300"}>{tc.actual_output || "(no output)"}</span></p>
          {tc.timed_out && <p className="text-amber-400">Time Limit Exceeded</p>}
          {tc.compile_error && <p className="text-rose-400">Compile Error</p>}
          {tc.stderr && <p className="text-slate-500 whitespace-pre-wrap">stderr: {tc.stderr.slice(0, 300)}</p>}
        </div>
      )}
    </div>
  );
}

export default function CodePlaygroundPanel({ problem, code, language, onCodeChange, onLanguageChange, onRun, running, result, languages }) {
  return (
    <div className="mt-4" data-testid="code-playground-panel">
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${DIFFICULTY_COLOR[problem?.difficulty] || DIFFICULTY_COLOR.Medium}`} data-testid="coding-difficulty-badge">
          {problem?.difficulty || "Medium"}
        </span>
        <select
          value={language}
          onChange={(e) => onLanguageChange(e.target.value)}
          data-testid="coding-language-select"
          className="bg-white/[0.05] border border-white/10 rounded-lg text-xs text-slate-200 px-2.5 py-1.5 focus:outline-none focus:border-indigo-400/40"
        >
          {(languages || []).map((l) => <option key={l.id} value={l.id} className="bg-[#0f1522]">{l.label}</option>)}
        </select>
      </div>

      <div className="whitespace-pre-wrap text-sm text-slate-300 leading-relaxed bg-white/[0.02] border border-white/10 rounded-xl p-4 mb-3" data-testid="coding-problem-statement">
        {problem?.problem_statement}
      </div>

      <div className="glass-panel rounded-2xl overflow-hidden border border-white/10">
        <div className="border-b border-white/10">
          <Editor
            height="320px"
            language={MONACO_LANG[language] || "python"}
            theme="vs-dark"
            value={code}
            onChange={(v) => onCodeChange(v || "")}
            options={{ minimap: { enabled: false }, fontSize: 13, lineNumbers: "on", scrollBeyondLastLine: false }}
            data-testid="coding-monaco-editor"
          />
        </div>
        <div className="p-3 flex items-center justify-between">
          <button
            onClick={onRun}
            disabled={running || !code?.trim()}
            data-testid="coding-run-btn"
            className="flex items-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
          >
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Run Sample Tests
          </button>
          {result && (
            <span className="flex items-center gap-1.5 text-xs font-semibold" data-testid="coding-run-summary">
              <Terminal className="w-3.5 h-3.5 text-slate-400" />
              <span className={result.all_passed ? "text-emerald-400" : "text-amber-400"}>{result.passed_count}/{result.total_count} passed</span>
            </span>
          )}
        </div>
      </div>

      {result && (
        <div className="mt-3 space-y-2" data-testid="coding-test-results-list">
          {result.results.map((tc, i) => <TestCaseRow key={i} tc={tc} idx={i} />)}
        </div>
      )}
    </div>
  );
}
